import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { Input } from '@/components/Input';
import { Link } from '@/components/Link';
import { Pill } from '@/components/Pill';
import { Skeleton } from '@/components/Skeleton';
import * as shared from '../../tasks.css';
import type { ReportingTaskSummary } from './types';

type TaskOverviewProps = {
    task: ReportingTaskSummary;
};

/**
 * Header block of the task-detail page: client name, task type (with a link to
 * the related survey project for survey tasks), and the disabled survey-name /
 * owner fields.
 */
export function TaskOverview({ task }: TaskOverviewProps) {
    const { name, userName, organization } = task;

    return (
        <>
            {organization && (
                <div>
                    <div className={shared.sectionLabel}>クライアント名</div>
                    <div className={shared.orgName}>{organization.name}</div>
                </div>
            )}

            <Flex direction="column" align="start" gap="2">
                <span className={shared.sectionLabel}>タスク種別</span>
                <Pill variant="secondary">{task.type.displayName}</Pill>

                {/*
                 * The survey project is nulled out when it is deleted, so an
                 * overwrite task can outlive its target. Drop the link entirely
                 * in that case — there is nothing left to navigate to.
                 */}
                {task.type.id === 'OVERWRITE_SURVEY' &&
                    (task.survey && organization ? (
                        <Link
                            to="/system/surveys"
                            search={{ organizationId: organization.uuid, surveyId: task.survey.surveyId }}
                        >
                            {task.survey.name}
                        </Link>
                    ) : (
                        <span className={shared.missingSurvey}>上書き対象のサーベイが見つかりません</span>
                    ))}

                {task.type.id === 'NEW_SURVEY' && task.survey && organization && (
                    <Link
                        to="/system/surveys"
                        search={{ organizationId: organization.uuid, surveyId: task.survey.surveyId }}
                    >
                        {task.survey.name}
                    </Link>
                )}
            </Flex>

            <Form.Row>
                <Form.Field
                    label="サーベイ名"
                    control={(props) => <Input {...props} type="text" defaultValue={name ?? ''} disabled />}
                />
                <Form.Field
                    label="担当者"
                    control={(props) => <Input {...props} type="text" defaultValue={userName ?? ''} disabled />}
                />
            </Form.Row>
        </>
    );
}

export function TaskOverviewSkeleton() {
    return (
        <>
            <div>
                <div className={shared.sectionLabel}>クライアント名</div>
                <Skeleton.Line width={160} height="lg" />
            </div>

            <Flex direction="column" align="start" gap="2">
                <span className={shared.sectionLabel}>タスク種別</span>
                <Skeleton.Box width={100} height={28} />
            </Flex>

            <Form.Row>
                <Form.FieldSkeleton />
                <Form.FieldSkeleton />
            </Form.Row>
        </>
    );
}
